import { Injectable } from '@angular/core';

import { indentPerLevel } from './constants';
import { Instruction } from './types';

export type ItemMode = 'standard' | 'expanded' | 'last-in-group';

interface Position {
  x: number;
  y: number;
}

@Injectable({
  providedIn: 'root',
})
export class HitboxService {
  private getCenter(rect: DOMRect): Position {
    return { x: (rect.right + rect.left) / 2, y: (rect.bottom + rect.top) / 2 };
  }

  private standardHitbox({
    client,
    borderBox,
  }: {
    client: Position;
    borderBox: DOMRect;
  }): 'reorder-above' | 'reorder-below' | 'make-child' {
    const quarterOfHeight = borderBox.height / 4;

    if (client.y <= borderBox.top + quarterOfHeight) {
      return 'reorder-above';
    }
    if (client.y >= borderBox.bottom - quarterOfHeight) {
      return 'reorder-below';
    }
    return 'make-child';
  }

  public getInstruction({
    client,
    borderBox,
    currentLevel,
    mode,
  }: {
    client: Position;
    borderBox: DOMRect;
    currentLevel: number;
    mode: ItemMode;
  }): Instruction {
    if (mode === 'standard') {
      const type = this.standardHitbox({ borderBox, client });
      return { type, indentPerLevel, currentLevel };
    }

    const center: Position = this.getCenter(borderBox);

    if (mode === 'expanded') {
      const type = this.standardHitbox({ borderBox, client });
      return {
        type: type === 'reorder-above' ? type : 'make-child',
        indentPerLevel,
        currentLevel,
      };
    }

    const visibleInset = indentPerLevel * currentLevel;

    if (client.x < borderBox.left + visibleInset) {
      if (client.y < center.y) {
        return { type: 'reorder-above', indentPerLevel, currentLevel };
      }
      const rawLevel = (client.x - borderBox.left) / indentPerLevel;
      const desiredLevel = Math.max(Math.floor(rawLevel), 0);
      return { type: 'reparent', desiredLevel, indentPerLevel, currentLevel };
    }

    return {
      type: this.standardHitbox({ borderBox, client }),
      indentPerLevel,
      currentLevel,
    };
  }

  public applyInstructionBlock(desired: Instruction): Instruction {
    if (desired.type !== 'instruction-blocked') {
      const blocked: Instruction = {
        type: 'instruction-blocked',
        desired,
      };
      return blocked;
    }

    return desired;
  }
}
